import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaPhone, FaInstagram, FaPaperPlane } from 'react-icons/fa';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import { useAuth } from '../provider/auth';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { toast } from 'react-toastify';

const Contact = () => {
    const { user } = useAuth();
    const [boardMembers, setBoardMembers] = useState([]);
    const [topics, setTopics] = useState([]);
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ topic: '', message: '' });

    const API_BASE = `${API_BASE_URL}/api`;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${API_BASE}/contact/data`);
                setBoardMembers((res.data.members || []).sort((a, b) => a.order - b.order));
                setTopics(res.data.topics || []);
                setSettings(res.data.settings || {});
                if (res.data.topics?.length > 0) {
                    setFormData(prev => ({ ...prev, topic: res.data.topics[0].value }));
                }
            } catch (err) {
                console.error('Error fetching contact data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [API_BASE]);

    const getDisplayName = (m) => {
        if (!m) return '';
        if (m.playerId && typeof m.playerId === 'object') {
            if (m.playerId.firstName || m.playerId.lastName) {
                return `${m.playerId.firstName || ''} ${m.playerId.lastName || ''}`.trim();
            }
        }
        return m.name || '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const selectedTopicLabel = topics.find(t => t.value === formData.topic)?.label;
            
            await axios.post(`${API_BASE}/contact/send-email`, {
                topic: formData.topic,
                message: formData.message,
                senderName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name,
                senderEmail: user?.email
            }, { withCredentials: true });

            toast.success(`Deine Nachricht zum Thema "${selectedTopicLabel}" wurde gesendet.`);
            setFormData(prev => ({ ...prev, message: '' }));
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Fehler beim Senden der E-Mail.';
            toast.error(errorMsg);
        }
    };

    if (loading) return <div className="p-5 text-center">Lädt...</div>;

    const heroImage = settings.boardImagePath?.startsWith('uploads') 
        ? `${API_BASE_URL}/${settings.boardImagePath}`
        : null; // No default image

    return (
        <div className="contact-page pb-5">
            {/* Hero Section - only show if image exists */}
            {heroImage && (
                <div className="vorstand-container d-flex justify-content-center mb-5">
                    <img src={heroImage} alt="Vorstand" className="vorstand-standalone-img rounded-4 shadow-lg" />
                </div>
            )}

            <div>
                {/* Board Group */}
                <div className="mb-5">
                    <Row className="g-4 justify-content-center">
                        {boardMembers.map((member, mIdx) => (
                            <Col key={mIdx} xs={6} sm={6} md={4} lg={3}>
                                <Card className="contact-card h-100 border-0 shadow-sm text-center p-3 glass-card">
                                    <div className="member-avatar mx-auto mb-3 bg-danger text-white d-flex align-items-center justify-content-center rounded-circle fs-4">
                                        {getDisplayName(member).charAt(0) || '?'}
                                    </div>
                                    <Card.Title className="fw-bold mb-1">{getDisplayName(member)}</Card.Title>
                                    <Card.Text className="text-muted small mb-3">{member.position}</Card.Text>
                                    <div className="contact-icons d-flex justify-content-center gap-3">
                                        {member.email && <a href={`mailto:${member.email}`} title={member.email} className="icon-link"><FaEnvelope size={18} /></a>}
                                        {member.phone && <a href={`tel:${member.phone}`} title={member.phone} className="icon-link"><FaPhone size={18} /></a>}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Bottom Section: Centered Form */}
                <Row className="mt-5 justify-content-center">
                    <Col lg={6}>
                        <div className="form-container p-4 rounded-4 shadow-sm glass-card border-0">
                            <h3 className="mb-2 fw-bold text-center"><FaPaperPlane className="me-2 text-danger" /> Schick uns eine Nachricht</h3>
                            <p className="text-muted small mb-4 text-center">Hallo <strong>{user?.firstName || 'Mitglied'}</strong>, wie können wir dir helfen?</p>
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-semibold">Thema</Form.Label>
                                    <Form.Select 
                                        className="form-input"
                                        value={formData.topic}
                                        onChange={(e) => setFormData({...formData, topic: e.target.value})}
                                    >
                                        {topics.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-4">
                                    <Form.Label className="small fw-semibold">Nachricht</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={5} 
                                        className="form-input" 
                                        placeholder="Deine Nachricht an den Vorstand..."
                                        value={formData.message}
                                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                                        required 
                                    />
                                    {(() => {
                                        const selectedTopic = topics.find(t => t.value === formData.topic);
                                        if (selectedTopic?.hint) {
                                            return (
                                                <Form.Text className="text-muted small mt-2 d-block">
                                                    <strong>Hinweis:</strong> {selectedTopic.hint}
                                                </Form.Text>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Form.Group>
                                <Button variant="danger" type="submit" className="w-100 rounded-pill py-2 fw-bold shadow-sm">
                                    Absenden
                                </Button>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Contact;
  
